<template>
  <div
    :class="['chat-ai-msg-wrap', { 'chat-ai-msg-pulse': pulse }]"
    :data-qid="msg.role === 'user' && qIndex ? qIndex : null"
  >
    <div
      v-if="msg.role === 'user' && qIndex"
      class="chat-ai-q-divider"
      aria-hidden="true"
    >
      <span class="chat-ai-q-divider-pill">Q{{ qIndex }}</span>
    </div>
    <div
      :class="['chat-ai-msg', 'chat-ai-msg-' + msg.role, { 'chat-ai-msg-streaming': msg.streaming, 'chat-ai-msg-error': msg.error }]"
      :data-component-id="`chat--msg--${msg.role}`"
    >
    <div
      class="chat-ai-msg-avatar"
      :class="[
        'chat-ai-msg-avatar-' + msg.role,
        { 'is-streaming': msg.streaming && msg.role === 'assistant' }
      ]"
    >
      <span class="chat-ai-msg-avatar-glyph">{{ msg.role === 'user' ? '🧑' : '✨' }}</span>
      <span v-if="msg.streaming && msg.role === 'assistant'" class="chat-ai-msg-avatar-ring" aria-hidden="true"></span>
    </div>
    <div class="chat-ai-msg-col">
      <div class="chat-ai-msg-meta">
        <span class="chat-ai-msg-name">{{ msg.role === 'user' ? 'Anda' : 'Yustian Smart AI' }}</span>
        <span v-if="msg.createdAt" class="chat-ai-msg-time" :title="absoluteTime(msg.createdAt)">
          {{ relativeTime(msg.createdAt, nowTick) }}
        </span>
      </div>
      <div class="chat-ai-msg-body">
        <!-- Premium streaming overlays: 4 floating sparkles + LIVE pill (Pass 4 + 5).
             Pure-CSS, di-render hanya saat assistant streaming.
             LIVE pill DISEMBUNYIKAN saat thinking-only (content kosong) supaya
             tidak tumpang tindih dengan badge elapsed "memikirkan jawaban · Xs"
             yang sudah memberi sinyal "live" sendiri. Pill hanya muncul saat
             content sudah mulai streaming (bubble lebih lebar, ada ruang). -->
        <template v-if="msg.role === 'assistant' && msg.streaming">
          <span class="chat-ai-bubble-spark spark-a" aria-hidden="true"></span>
          <span class="chat-ai-bubble-spark spark-b" aria-hidden="true"></span>
          <span class="chat-ai-bubble-spark spark-c" aria-hidden="true"></span>
          <span class="chat-ai-bubble-spark spark-d" aria-hidden="true"></span>
          <span v-if="msg.content" class="chat-ai-bubble-live" aria-hidden="true">
            <span class="chat-ai-bubble-live-dot"></span>LIVE
          </span>
        </template>
        <template v-if="msg.role === 'assistant'">
          <MarkdownRenderer
            v-if="msg.content"
            :content="msg.content"
            :streaming="!!msg.streaming"
            :glossaryMap="glossaryMap"
            @gloss-click="onGlossClick"
            @gloss-hover="onGlossHover"
            @gloss-leave="onGlossLeave"
          />
          <!-- Fallback: stream ended without content and no error set — show placeholder + retry -->
          <div v-if="!msg.content && !msg.streaming && !msg.error" class="chat-ai-empty-fallback">
            <span class="chat-ai-empty-fallback-dots">·· ·</span>
            <button class="chat-ai-empty-fallback-retry" @click="$emit('retry')" title="Coba kirim ulang">
              ↻ Ulangi
            </button>
          </div>
          <div v-if="msg.streaming && !msg.content" class="chat-ai-thinking">
            <span class="chat-ai-thinking-text">memikirkan jawaban</span>
            <span class="chat-ai-thinking-dots" aria-hidden="true">
              <span></span><span></span><span></span>
            </span>
            <span class="chat-ai-thinking-elapsed">{{ thinkingElapsedLabel }}</span>
          </div>
          <span v-if="msg.streaming && msg.content" class="chat-ai-cursor">▍</span>
        </template>
        <template v-else>
          <div v-if="msg.images && msg.images.length" class="chat-ai-user-images">
            <a
              v-for="(img, i) in msg.images"
              :key="i"
              :href="img.dataUrl"
              target="_blank"
              rel="noopener"
              class="chat-ai-user-image-link"
              :title="img.name || 'Buka gambar'"
            >
              <img :src="img.dataUrl" :alt="img.name || `image ${i+1}`" />
            </a>
          </div>
          <div v-if="msg.content" class="chat-ai-user-text">{{ msg.content }}</div>
        </template>
      </div>
      <div v-if="!msg.streaming && msg.content" class="chat-ai-msg-actions">
        <span
          v-if="msg.role === 'assistant' && glossEnriching"
          class="chat-ai-msg-gloss-loading"
          title="Mengenali istilah teknis di jawaban ini…"
        >
          <span class="chat-ai-msg-gloss-loading-dot"></span>
          mengenali istilah
        </span>
        <span
          v-else-if="msg.role === 'assistant' && glossTermCount > 0"
          class="chat-ai-msg-gloss-count"
          :title="`${glossTermCount} istilah dengan penjelasan — klik kata bergaris bawah untuk lihat`"
        >
          📚 {{ glossTermCount }} istilah
        </span>
        <button class="chat-ai-msg-action" @click="$emit('copy', msg)" title="Salin">
          <span>{{ copied ? '✓' : '📋' }}</span>
          <span>{{ copied ? 'Tersalin' : 'Salin' }}</span>
        </button>
        <button
          v-if="msg.error && msg.role === 'assistant'"
          class="chat-ai-msg-action chat-ai-msg-action-retry"
          @click="$emit('retry')"
          title="Coba lagi"
        >
          <span>↻</span><span>Ulangi</span>
        </button>
        <button
          v-if="msg.messageId && msg.role === 'assistant' && !msg.error"
          class="chat-ai-msg-action chat-ai-msg-action-fork"
          @click="$emit('fork', msg)"
          title="Cabang percakapan dari titik ini"
          data-component-id="chat--msg--btn-fork"
        >
          <span>🔱</span><span>Cabang</span>
        </button>
      </div>
      <!-- Provenance: jejak tool (mis. Google Ads) yang dipakai untuk jawaban ini.
           Default collapsed bila >1 langkah; bisa di-expand untuk audit angka. -->
      <ChatToolTimeline
        v-if="msg.role === 'assistant' && !msg.streaming && msg.tools && msg.tools.length"
        :activity="msg.tools"
        :is-streaming="false"
        label="Sumber data"
        embedded
        :default-collapsed="msg.tools.length > 1"
      />
    </div>
    </div>
  </div>

  <GlossaryPopup
    :term="glossPopup.term"
    :entry="glossPopup.entry"
    :visible="glossPopup.visible"
    :anchorRect="glossPopup.rect"
    @close="closeGlossPopup"
    @hover-in="onPopupHoverIn"
    @hover-out="onPopupHoverOut"
  />
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import MarkdownRenderer from './MarkdownRenderer.vue'
import GlossaryPopup from './GlossaryPopup.vue'
import ChatToolTimeline from './ChatToolTimeline.vue'
import { useGlossary } from '../composables/useGlossary.js'

const props = defineProps({
  msg:     { type: Object,  required: true },
  copied:  { type: Boolean, default: false },
  nowTick: { type: Number,  default: 0 },
  qIndex:  { type: Number,  default: null },
  pulse:   { type: Boolean, default: false },
})
defineEmits(['copy', 'retry', 'fork'])

// ── Glossary ─────────────────────────────────────────────────────────────────
const { enrich } = useGlossary()

const glossaryMap   = ref(null) // { term -> { definition, example, category } }
const glossPopup    = ref({ visible: false, term: null, entry: null, rect: null, pinned: false })
const glossEnriching = ref(false)
let enrichTriggered = false

const glossTermCount = computed(() =>
  glossaryMap.value ? Object.keys(glossaryMap.value).length : 0
)

// Trigger enrichment once streaming ends on assistant messages
watch(
  () => props.msg.streaming,
  (isStreaming) => {
    if (!isStreaming && props.msg.role === 'assistant' && props.msg.content && !enrichTriggered) {
      enrichTriggered = true
      glossEnriching.value = true
      enrich(props.msg.content)
        .then(map => {
          if (map && Object.keys(map).length > 0) glossaryMap.value = map
        })
        .finally(() => { glossEnriching.value = false })
    }
  },
  { immediate: true },
)

function resolveGlossEntry(term) {
  const map = glossaryMap.value
  if (!map) return null
  let entry = map[term] || null
  if (!entry) {
    const lower = term.toLowerCase()
    for (const [k, v] of Object.entries(map)) {
      if (k.toLowerCase() === lower) { entry = v; break }
    }
  }
  return entry
}

function onGlossClick({ term, rect }) {
  const entry = resolveGlossEntry(term)
  if (!entry) return
  glossPopup.value = { visible: true, term, entry, rect, pinned: true }
}

function onGlossHover({ term, rect }) {
  // Kalau popup sudah pinned (klik), jangan diganggu hover
  if (glossPopup.value.pinned) return
  const entry = resolveGlossEntry(term)
  if (!entry) return
  clearPopupCloseTimer()
  glossPopup.value = { visible: true, term, entry, rect, pinned: false }
}

let popupCloseTimer = null
function clearPopupCloseTimer() {
  if (popupCloseTimer) { clearTimeout(popupCloseTimer); popupCloseTimer = null }
}
function scheduleHoverClose() {
  clearPopupCloseTimer()
  popupCloseTimer = setTimeout(() => {
    if (!glossPopup.value.pinned) {
      glossPopup.value = { ...glossPopup.value, visible: false }
    }
    popupCloseTimer = null
  }, 220)
}

function onGlossLeave() {
  if (glossPopup.value.pinned) return
  scheduleHoverClose()
}

function onPopupHoverIn() {
  // Mouse masuk ke popup → batalkan rencana close, biar bisa baca isinya
  clearPopupCloseTimer()
}
function onPopupHoverOut() {
  if (glossPopup.value.pinned) return
  scheduleHoverClose()
}

function closeGlossPopup() {
  clearPopupCloseTimer()
  glossPopup.value = { ...glossPopup.value, visible: false, pinned: false }
}

// Live elapsed counter untuk thinking bubble. Tick 500ms hanya saat AI sedang
// streaming dan belum keluar konten apa-apa. Saat content muncul atau
// streaming selesai, interval dihentikan supaya tidak boros render.
const thinkingTick = ref(Date.now())
let _thinkInterval = null

const thinkingActive = computed(() =>
  props.msg.role === 'assistant' && !!props.msg.streaming && !props.msg.content
)

watch(thinkingActive, (active) => {
  if (active) {
    if (_thinkInterval) return
    thinkingTick.value = Date.now()
    _thinkInterval = setInterval(() => { thinkingTick.value = Date.now() }, 500)
  } else if (_thinkInterval) {
    clearInterval(_thinkInterval); _thinkInterval = null
  }
}, { immediate: true })

onBeforeUnmount(() => {
  if (_thinkInterval) clearInterval(_thinkInterval)
})

const thinkingElapsedLabel = computed(() => {
  const start = props.msg.createdAt || thinkingTick.value
  const ms = Math.max(0, thinkingTick.value - start)
  const totalSec = Math.floor(ms / 1000)
  if (totalSec < 60) return `${totalSec}s`
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  return `${m}:${String(s).padStart(2, '0')}`
})

function relativeTime(ts, _tick) {
  if (!ts) return ''
  const diff = Date.now() - ts
  if (diff < 5000) return 'baru saja'
  const sec = Math.floor(diff / 1000)
  if (sec < 60) return `${sec}d`
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}j`
  return new Date(ts).toLocaleDateString('id-ID', { day: '2-digit', month: 'short' })
}
function absoluteTime(ts) {
  if (!ts) return ''
  return new Date(ts).toLocaleString('id-ID', { dateStyle: 'medium', timeStyle: 'short' })
}
</script>

<style scoped>
/* ── Wrapper + Q-divider ─────────────────────────────────────────────────── */
.chat-ai-msg-wrap {
  display: flex;
  flex-direction: column;
  gap: 6px;
  scroll-margin-top: 64px; /* jangan menempel header saat di-jump */
  border-radius: 10px;
  transition: background 0.4s ease, box-shadow 0.4s ease;
}
.chat-ai-msg-pulse {
  background: var(--accent-soft, rgba(59, 130, 246, 0.10));
  box-shadow: 0 0 0 2px var(--accent, var(--accent-blue, #3b82f6));
  animation: chat-ai-pulse 1.4s ease;
}
@keyframes chat-ai-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.55); }
  60%  { box-shadow: 0 0 0 8px rgba(59, 130, 246, 0); }
  100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0); }
}

.chat-ai-q-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 10px 0 -2px;
  user-select: none;
}
.chat-ai-q-divider::before,
.chat-ai-q-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    color-mix(in srgb, var(--accent-blue, #3b82f6) 30%, transparent) 30%,
    color-mix(in srgb, var(--accent-blue, #3b82f6) 30%, transparent) 70%,
    transparent
  );
}
.chat-ai-q-divider-pill {
  background:
    linear-gradient(135deg,
      color-mix(in srgb, var(--accent-blue, #3b82f6) 14%, var(--bg-card, #111827)),
      color-mix(in srgb, var(--accent-purple, #8b5cf6) 12%, var(--bg-card, #111827)));
  border: 1px solid color-mix(in srgb, var(--accent-blue, #3b82f6) 35%, transparent);
  border-radius: 10px;
  padding: 2px 10px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.7px;
  color: var(--accent-blue-light, #7db4f5);
  font-variant-numeric: tabular-nums;
  text-transform: uppercase;
  box-shadow: 0 0 12px color-mix(in srgb, var(--accent-blue, #3b82f6) 18%, transparent);
}

.chat-ai-msg {
  display: flex;
  gap: 9px;
  align-items: flex-start;
  max-width: 100%;
}
.chat-ai-msg-user { flex-direction: row-reverse; }

.chat-ai-msg-avatar {
  position: relative;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  flex-shrink: 0;
  font-size: 14px;
  border: 1px solid var(--border-soft, var(--border-light));
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
}
.chat-ai-msg-avatar-glyph {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
}
.chat-ai-msg-avatar-user {
  background: linear-gradient(135deg, var(--accent-soft, rgba(59,130,246,0.18)), rgba(139, 92, 246, 0.18));
  border-color: var(--accent, var(--accent-blue, #3b82f6));
}
.chat-ai-msg-avatar-assistant {
  background: linear-gradient(135deg, var(--bg-elev, var(--bg-card)), rgba(139, 92, 246, 0.12));
  border-color: color-mix(in srgb, var(--accent-purple, #8b5cf6) 30%, var(--border-soft, #1e2a3e));
}
/* ─── Pass 4 — premium avatar: conic ring + counter-rotating glyph + ping ──── */
@property --chat-avatar-angle {
  syntax: '<angle>';
  initial-value: 0deg;
  inherits: false;
}
.chat-ai-msg-avatar.is-streaming {
  border: 1.5px solid transparent;
  background:
    linear-gradient(
      135deg,
      var(--bg-elev, var(--bg-card, #111827)),
      rgba(139, 92, 246, 0.16)
    ) padding-box,
    conic-gradient(
      from var(--chat-avatar-angle, 0deg),
      var(--accent-blue, #3b82f6),
      var(--accent-purple, #8b5cf6),
      var(--accent-blue-light, #7db4f5),
      var(--accent-purple, #8b5cf6),
      var(--accent-blue, #3b82f6)
    ) border-box;
  box-shadow:
    0 0 0 2px color-mix(in srgb, var(--accent-blue, #3b82f6) 14%, transparent),
    0 0 16px color-mix(in srgb, var(--accent-blue, #3b82f6) 35%, transparent),
    0 0 28px color-mix(in srgb, var(--accent-purple, #8b5cf6) 18%, transparent);
  animation:
    chat-ai-avatar-conic 3.8s linear infinite,
    chat-ai-avatar-breath 2.6s ease-in-out infinite;
}
.chat-ai-msg-avatar.is-streaming .chat-ai-msg-avatar-glyph {
  animation: chat-ai-avatar-bob 2.6s ease-in-out infinite;
  filter: drop-shadow(0 0 4px color-mix(in srgb, var(--accent-purple, #8b5cf6) 60%, transparent));
}
/* The "ring" element acts as ping wave + outer halo, dual-layered. */
.chat-ai-msg-avatar-ring {
  position: absolute;
  inset: -4px;
  border-radius: 50%;
  border: 1.5px solid color-mix(in srgb, var(--accent-blue, #3b82f6) 70%, transparent);
  opacity: 0;
  animation: chat-ai-avatar-ping 2s ease-out infinite;
  pointer-events: none;
}
.chat-ai-msg-avatar-ring::after {
  content: '';
  position: absolute;
  inset: -3px;
  border-radius: 50%;
  border: 1.5px dashed color-mix(in srgb, var(--accent-purple, #8b5cf6) 50%, transparent);
  opacity: 0.35;
  animation: chat-ai-avatar-orbit 6s linear infinite;
}
@keyframes chat-ai-avatar-conic {
  to { --chat-avatar-angle: 360deg; }
}
@keyframes chat-ai-avatar-breath {
  0%, 100% {
    box-shadow:
      0 0 0 2px color-mix(in srgb, var(--accent-blue, #3b82f6) 12%, transparent),
      0 0 14px color-mix(in srgb, var(--accent-blue, #3b82f6) 28%, transparent),
      0 0 24px color-mix(in srgb, var(--accent-purple, #8b5cf6) 14%, transparent);
  }
  50% {
    box-shadow:
      0 0 0 3px color-mix(in srgb, var(--accent-blue, #3b82f6) 22%, transparent),
      0 0 22px color-mix(in srgb, var(--accent-blue, #3b82f6) 50%, transparent),
      0 0 40px color-mix(in srgb, var(--accent-purple, #8b5cf6) 24%, transparent);
  }
}
@keyframes chat-ai-avatar-bob {
  0%, 100% { transform: translateY(0)    rotate(0deg); }
  50%      { transform: translateY(-1px) rotate(8deg); }
}
@keyframes chat-ai-avatar-ping {
  0%        { transform: scale(0.85); opacity: 0.7; }
  80%, 100% { transform: scale(1.7);  opacity: 0; }
}
@keyframes chat-ai-avatar-orbit {
  to { transform: rotate(-360deg); }
}
@media (prefers-reduced-motion: reduce) {
  .chat-ai-msg-avatar.is-streaming { animation: none; }
  .chat-ai-msg-avatar-ring,
  .chat-ai-msg-avatar-ring::after { animation: none; }
}

.chat-ai-msg-col {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  max-width: calc(100% - 44px);
}
.chat-ai-msg-user .chat-ai-msg-col { align-items: flex-end; }

.chat-ai-msg-meta {
  display: inline-flex;
  align-items: baseline;
  gap: 7px;
  font-size: 10.5px;
  padding: 0 4px;
}
.chat-ai-msg-name {
  font-weight: 600;
  color: var(--text-base, var(--text-body));
  letter-spacing: 0.1px;
}
.chat-ai-msg-time {
  color: var(--text-dim, #6b7a8d);
  font-variant-numeric: tabular-nums;
}

.chat-ai-msg-body {
  position: relative;
  background: var(--bg-elev, var(--bg-card));
  border: 1px solid var(--border-soft, var(--border-light));
  border-radius: 10px;
  padding: 9px 13px;
  word-wrap: break-word;
  overflow-wrap: anywhere;
  line-height: 1.55;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
}
.chat-ai-msg-user .chat-ai-msg-body {
  background: linear-gradient(135deg, var(--accent, var(--accent-blue, #3b82f6)), var(--accent-purple, #8b5cf6));
  border-color: transparent;
  border-bottom-right-radius: 3px;
  color: #fff;
}
.chat-ai-msg-assistant .chat-ai-msg-body { border-bottom-left-radius: 3px; }
/* ─── PREMIUM STREAMING BUBBLE — 5 layered passes ────────────────────────────
   Pass 1: Conic-rotating border (mask-composite trick + @property angle).
   Pass 2: Glassy depth via inner radial highlight + multi-stop bg.
   Pass 3: Multi-layer ambient breathing glow stack.
   Pass 4: LIVE pill + premium type/cursor (avatar handled separately).
   Pass 5: Floating sparkle particles in 4 positions, drift + fade.
*/

/* @property registers the angle as a typed CSS variable so it can be animated
   smoothly via keyframes (otherwise conic-gradient `from <angle>` won't tween). */
@property --chat-bubble-angle {
  syntax: '<angle>';
  initial-value: 0deg;
  inherits: false;
}

.chat-ai-msg-streaming .chat-ai-msg-body {
  position: relative;
  isolation: isolate; /* keep ::before/::after stacking inside this bubble */
  /* Pass 2: glassy multi-stop background — soft accent tint at top, fade to bg.
     Pass 1 base: transparent border, replaced by ::before conic. */
  border: 1.5px solid transparent;
  background:
    radial-gradient(
      120% 80% at 50% -20%,
      color-mix(in srgb, var(--accent-blue, #3b82f6) 8%, transparent) 0%,
      transparent 55%
    ),
    linear-gradient(
      135deg,
      color-mix(in srgb, var(--accent-blue, #3b82f6) 4%, var(--bg-card, #111827)),
      var(--bg-card, #111827) 70%
    );
  /* Pass 3: stacked breathing glow — 4 layers at different blur radii. */
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.025) inset,
    0 4px 14px rgba(0, 0, 0, 0.28),
    0 0 22px color-mix(in srgb, var(--accent-blue, #3b82f6) 18%, transparent),
    0 0 44px color-mix(in srgb, var(--accent-purple, #8b5cf6) 12%, transparent);
  animation: chat-bubble-ambient 4.4s ease-in-out infinite;
}

/* Pass 1: conic-rotating border using mask-composite (border-only, no clip). */
.chat-ai-msg-streaming .chat-ai-msg-body::before {
  content: '';
  position: absolute;
  inset: -1.5px; /* match border thickness */
  border-radius: inherit;
  padding: 1.5px;
  background: conic-gradient(
    from var(--chat-bubble-angle, 0deg),
    var(--accent-blue, #3b82f6) 0deg,
    var(--accent-purple, #8b5cf6) 90deg,
    var(--accent-blue-light, #7db4f5) 180deg,
    var(--accent-purple, #8b5cf6) 270deg,
    var(--accent-blue, #3b82f6) 360deg
  );
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
          mask-composite: exclude;
  animation: chat-bubble-rotate 5s linear infinite;
  z-index: -1;
  pointer-events: none;
}

/* Pass 2 cont. — top inner highlight gloss for "glass" feel. */
.chat-ai-msg-streaming .chat-ai-msg-body::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  background:
    linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.05) 0%,
      transparent 28%
    );
  z-index: 0;
}

/* Konten ditarik di atas overlay decorative. */
.chat-ai-msg-streaming .chat-ai-msg-body > :not(.chat-ai-bubble-spark):not(.chat-ai-bubble-live) {
  position: relative;
  z-index: 2;
}

@keyframes chat-bubble-rotate {
  to { --chat-bubble-angle: 360deg; }
}
@keyframes chat-bubble-ambient {
  0%, 100% {
    box-shadow:
      0 0 0 1px rgba(255, 255, 255, 0.025) inset,
      0 4px 14px rgba(0, 0, 0, 0.28),
      0 0 20px color-mix(in srgb, var(--accent-blue, #3b82f6) 16%, transparent),
      0 0 38px color-mix(in srgb, var(--accent-purple, #8b5cf6) 10%, transparent);
  }
  50% {
    box-shadow:
      0 0 0 1px rgba(255, 255, 255, 0.04) inset,
      0 6px 18px rgba(0, 0, 0, 0.32),
      0 0 30px color-mix(in srgb, var(--accent-purple, #8b5cf6) 24%, transparent),
      0 0 56px color-mix(in srgb, var(--accent-blue, #3b82f6) 14%, transparent);
  }
}

/* Pass 4: LIVE pill — premium "ribbon" badge yang menempel di tepi atas
   bubble (top: -10px) sehingga TIDAK PERNAH menabrak konten teks meski
   bubble-nya kecil 1-line. Background solid (bg-card) supaya tidak
   transparan ke konic border di belakang. */
.chat-ai-bubble-live {
  position: absolute;
  top: -10px; right: 14px;
  display: inline-flex; align-items: center; gap: 5px;
  padding: 2px 8px 2px 6px;
  border-radius: 9px;
  font-size: 9px; font-weight: 700; letter-spacing: 0.9px;
  color: var(--accent-blue-light, #7db4f5);
  background: linear-gradient(
    180deg,
    color-mix(in srgb, var(--accent-blue, #3b82f6) 22%, var(--bg-card, #111827)),
    var(--bg-card, #111827)
  );
  border: 1px solid color-mix(in srgb, var(--accent-blue, #3b82f6) 45%, transparent);
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.35),
    0 0 12px color-mix(in srgb, var(--accent-blue, #3b82f6) 28%, transparent);
  z-index: 4;
  pointer-events: none;
  user-select: none;
}
.chat-ai-bubble-live-dot {
  width: 5px; height: 5px; border-radius: 50%;
  background: var(--accent-red, #ef4444);
  box-shadow: 0 0 6px var(--accent-red, #ef4444);
  animation: chat-bubble-live-pulse 1.2s ease-in-out infinite;
}
@keyframes chat-bubble-live-pulse {
  0%, 100% { opacity: 1;   transform: scale(1); }
  50%      { opacity: 0.4; transform: scale(0.7); }
}

/* Pass 5: floating sparkle particles — 4 tiny dots that drift up + fade. */
.chat-ai-bubble-spark {
  position: absolute;
  width: 3px; height: 3px;
  border-radius: 50%;
  background: var(--accent-blue-light, #7db4f5);
  box-shadow: 0 0 6px currentColor;
  color: var(--accent-blue-light, #7db4f5);
  pointer-events: none;
  opacity: 0;
  z-index: 1;
  animation: chat-bubble-spark-drift 4.5s ease-in-out infinite;
}
.chat-ai-bubble-spark.spark-a {
  left: 14%; bottom: 8%;
  background: var(--accent-blue, #3b82f6);
  color: var(--accent-blue, #3b82f6);
  animation-delay: 0s;
  animation-duration: 4.8s;
}
.chat-ai-bubble-spark.spark-b {
  left: 68%; bottom: 12%;
  background: var(--accent-purple, #8b5cf6);
  color: var(--accent-purple, #8b5cf6);
  animation-delay: 1.2s;
  animation-duration: 5.4s;
}
.chat-ai-bubble-spark.spark-c {
  left: 38%; bottom: 4%;
  background: var(--accent-blue-light, #7db4f5);
  color: var(--accent-blue-light, #7db4f5);
  animation-delay: 2.6s;
  animation-duration: 4.2s;
}
.chat-ai-bubble-spark.spark-d {
  left: 86%; bottom: 18%;
  width: 2px; height: 2px;
  background: var(--accent-purple, #8b5cf6);
  color: var(--accent-purple, #8b5cf6);
  animation-delay: 3.4s;
  animation-duration: 5.8s;
}
@keyframes chat-bubble-spark-drift {
  0%   { opacity: 0;   transform: translateY(0)    scale(0.6); }
  20%  { opacity: 0.9; transform: translateY(-18px) scale(1); }
  60%  { opacity: 0.6; transform: translateY(-44px) scale(1.1); }
  100% { opacity: 0;   transform: translateY(-72px) scale(0.4); }
}

/* Reduced motion: disable heavy animations, fall back to subtle steady glow. */
@media (prefers-reduced-motion: reduce) {
  .chat-ai-msg-streaming .chat-ai-msg-body { animation: none; }
  .chat-ai-msg-streaming .chat-ai-msg-body::before { animation-duration: 30s; }
  .chat-ai-bubble-spark { animation: none; opacity: 0.3; }
  .chat-ai-bubble-live-dot { animation: none; }
}
.chat-ai-msg-error .chat-ai-msg-body {
  border-color: rgba(239, 68, 68, 0.4);
  background: rgba(239, 68, 68, 0.06);
}
.chat-ai-user-text {
  white-space: pre-wrap;
  font-size: calc(13px * var(--chat-font-scale, 1));
  line-height: 1.5;
  color: #fff;
}

/* Inline images in user bubbles (pasted/attached) */
.chat-ai-user-images {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 6px;
}
.chat-ai-user-image-link {
  display: block;
  border-radius: 6px;
  overflow: hidden;
  max-width: 220px;
  background: rgba(0, 0, 0, 0.25);
  cursor: zoom-in;
  transition: transform 0.12s, box-shadow 0.12s;
}
.chat-ai-user-image-link:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
}
.chat-ai-user-image-link img {
  display: block;
  width: 100%;
  height: auto;
  max-height: 220px;
  object-fit: cover;
}

.chat-ai-msg-actions {
  display: flex;
  gap: 4px;
  margin-top: 4px;
  padding: 0 4px;
  opacity: 0;
  transition: opacity 0.18s;
}
.chat-ai-msg:hover .chat-ai-msg-actions { opacity: 1; }
.chat-ai-msg-action {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  border: 1px solid var(--border-soft, var(--border-light));
  color: var(--text-dim, #888);
  padding: 2px 7px;
  border-radius: 4px;
  font-size: 10.5px;
  cursor: pointer;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
  font-family: inherit;
}
.chat-ai-msg-action:hover {
  background: var(--bg-hover, var(--bg-hover));
  color: var(--text-base, var(--text-body));
  border-color: var(--border-accent, var(--border-soft));
}
.chat-ai-msg-action-retry:hover {
  color: var(--accent-blue-light, var(--accent, #3b82f6));
  border-color: var(--accent, var(--accent-blue, #3b82f6));
}
.chat-ai-msg-action-fork:hover {
  color: var(--accent-purple, #8b5cf6);
  border-color: var(--accent-purple, #8b5cf6);
}

/* ── Glossary status pill (di sebelah kiri tombol Salin) ───────────────────── */
.chat-ai-msg-gloss-loading,
.chat-ai-msg-gloss-count {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.2px;
  font-family: inherit;
  margin-right: 4px;
  user-select: none;
}
.chat-ai-msg-gloss-loading {
  color: var(--accent-blue-light, #93c5fd);
  background: color-mix(in srgb, var(--accent-blue, #3b82f6) 12%, transparent);
  border: 1px solid color-mix(in srgb, var(--accent-blue, #3b82f6) 35%, transparent);
}
.chat-ai-msg-gloss-loading-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: var(--accent-blue-light, #93c5fd);
  box-shadow: 0 0 6px var(--accent-blue-light, #93c5fd);
  animation: chat-ai-gloss-pulse 1.1s ease-in-out infinite;
}
@keyframes chat-ai-gloss-pulse {
  0%, 100% { opacity: 0.35; transform: scale(0.85); }
  50%      { opacity: 1;    transform: scale(1.15); }
}
.chat-ai-msg-gloss-count {
  color: var(--text-dim, #94a3b8);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--border-soft, rgba(255, 255, 255, 0.08));
  cursor: default;
}

.chat-ai-msg-body :deep(.md-content) { font-size: calc(13px * var(--chat-font-scale, 1)); }
.chat-ai-msg-body :deep(.md-content > *:first-child) { margin-top: 0; }
.chat-ai-msg-body :deep(.md-content > *:last-child)  { margin-bottom: 0; }
.chat-ai-msg-body :deep(p) { margin: 6px 0; }
.chat-ai-msg-body :deep(h1),
.chat-ai-msg-body :deep(h2),
.chat-ai-msg-body :deep(h3),
.chat-ai-msg-body :deep(h4) { margin: 11px 0 6px; font-weight: 600; letter-spacing: -0.1px; }
.chat-ai-msg-body :deep(ul),
.chat-ai-msg-body :deep(ol) { margin: 6px 0; padding-left: 22px; }
.chat-ai-msg-body :deep(li) { margin: 2px 0; }
.chat-ai-msg-body :deep(code) {
  background: rgba(255, 255, 255, 0.08);
  padding: 1px 5px;
  border-radius: 3px;
  font-size: calc(12px * var(--chat-font-scale, 1));
  font-family: 'Consolas', 'Monaco', monospace;
}
.chat-ai-msg-body :deep(table) {
  border-collapse: collapse;
  margin: 8px 0;
  font-size: calc(12px * var(--chat-font-scale, 1));
  width: 100%;
}
.chat-ai-msg-body :deep(th),
.chat-ai-msg-body :deep(td) {
  border: 1px solid var(--border-soft, var(--border-light));
  padding: 5px 9px;
  text-align: left;
}
.chat-ai-msg-body :deep(th) { background: var(--bg-hover, var(--bg-hover)); font-weight: 600; }
.chat-ai-msg-body :deep(blockquote) {
  border-left: 3px solid var(--accent, var(--accent-blue, #3b82f6));
  padding: 2px 12px;
  margin: 8px 0;
  background: var(--accent-soft, rgba(59, 130, 246, 0.06));
  border-radius: 0 6px 6px 0;
  color: var(--text-base, var(--text-body));
}
.chat-ai-msg-body :deep(a) {
  color: var(--accent-blue-light, var(--accent, #3b82f6));
  text-decoration: underline;
  text-underline-offset: 2px;
}
.chat-ai-msg-body :deep(a:hover) { opacity: 0.8; }

.chat-ai-empty-fallback {
  display: flex;
  align-items: center;
  gap: 10px;
  user-select: none;
}
.chat-ai-empty-fallback-dots {
  color: var(--text-dim, #6b7a8d);
  font-size: 11px;
  letter-spacing: 3px;
  opacity: 0.5;
}
.chat-ai-empty-fallback-retry {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  border: 1px solid var(--border-soft, var(--border-light));
  color: var(--text-dim, #888);
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10.5px;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
}
.chat-ai-empty-fallback-retry:hover {
  color: var(--accent-blue-light, #7db4f5);
  border-color: var(--accent-blue, #3b82f6);
  background: color-mix(in srgb, var(--accent-blue, #3b82f6) 8%, transparent);
}

.chat-ai-thinking {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 2px 0;
  font-size: 12.5px; font-weight: 500;
  letter-spacing: 0.1px;
}
.chat-ai-thinking-text {
  background: linear-gradient(
    90deg,
    var(--text-muted, #94a3b8) 0%,
    var(--accent-blue-light, #7db4f5) 45%,
    var(--accent-purple, #8b5cf6) 60%,
    var(--text-muted, #94a3b8) 100%);
  background-size: 220% 100%;
  -webkit-background-clip: text;
          background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: chat-ai-thinking-shimmer 2.6s linear infinite;
}
.chat-ai-thinking-dots {
  display: inline-flex; gap: 3px; align-items: center;
}
.chat-ai-thinking-dots span {
  width: 5px; height: 5px; border-radius: 50%;
  background: var(--accent-blue, #3b82f6);
  box-shadow: 0 0 6px color-mix(in srgb, var(--accent-blue, #3b82f6) 50%, transparent);
  animation: chat-ai-bubble-bounce 1.2s infinite ease-in-out;
}
.chat-ai-thinking-dots span:nth-child(2) { animation-delay: 0.15s; background: var(--accent-purple, #8b5cf6); box-shadow: 0 0 6px color-mix(in srgb, var(--accent-purple, #8b5cf6) 50%, transparent); }
.chat-ai-thinking-dots span:nth-child(3) { animation-delay: 0.3s;  background: var(--accent-blue-light, #7db4f5); box-shadow: 0 0 6px color-mix(in srgb, var(--accent-blue-light, #7db4f5) 50%, transparent); }
.chat-ai-thinking-elapsed {
  margin-left: 6px;
  padding: 1px 8px;
  border-radius: 9px;
  font-size: 10.5px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.3px;
  color: var(--accent-blue-light, #7db4f5);
  background: color-mix(in srgb, var(--accent-blue, #3b82f6) 10%, transparent);
  border: 1px solid color-mix(in srgb, var(--accent-blue, #3b82f6) 28%, transparent);
  animation: chat-ai-thinking-elapsed-pulse 1.4s ease-in-out infinite;
}
@keyframes chat-ai-thinking-elapsed-pulse {
  0%, 100% { opacity: 0.85; }
  50%      { opacity: 1; }
}
@keyframes chat-ai-bubble-bounce {
  0%, 80%, 100% { opacity: 0.35; transform: scale(0.7) translateY(0); }
  40%           { opacity: 1;    transform: scale(1)   translateY(-2px); }
}
@keyframes chat-ai-thinking-shimmer {
  from { background-position: 100% 0; }
  to   { background-position: -120% 0; }
}
.chat-ai-cursor {
  display: inline-block;
  color: var(--accent, var(--accent-blue, #3b82f6));
  animation: chat-ai-bubble-blink 1s infinite;
  margin-left: 2px;
}
@keyframes chat-ai-bubble-blink {
  0%, 50%   { opacity: 1; }
  51%, 100% { opacity: 0; }
}
</style>
